import { useState, useEffect, useCallback, useRef } from 'react'
import { useSsoToken } from './useSsoToken'
import { getClaimParticipants, getClaimAdhocContacts } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import type { ApiSlice } from './useClaimDetails'
import type { Contact } from '../types/contact'

const initialSlice = <T>(): ApiSlice<T> => ({ data: null, loading: false, error: null })

// A refetch keeps whatever is already on screen instead of blanking it: the contact
// list (and any expanded ad-hoc entry form) stays put while the refresh runs, so
// saving one contact doesn't tear the tab down and reset the others (MSD-255).
const toLoading = <T>(prev: ApiSlice<T>): ApiSlice<T> => ({ data: prev.data, loading: true, error: null })
const toError = <T>(prev: ApiSlice<T>, error: string): ApiSlice<T> => ({ data: prev.data, loading: false, error })

// Fetches a claim's MC1 participants and ad-hoc contacts (separate endpoints,
// combined client-side in ContactsTab).
export function useClaimContacts(claimNumber: string | null) {
  const { getToken } = useSsoToken()

  const [participants, setParticipants] = useState<ApiSlice<Contact[]>>(initialSlice)
  const [adhocContacts, setAdhocContacts] = useState<ApiSlice<Contact[]>>(initialSlice)

  // Each fetch gets an id; a response only lands if its fetch is still the latest,
  // so a late response can't clobber state after a claim swap, retry, or unmount.
  const fetchIdRef = useRef(0)

  const fetchContacts = useCallback(async () => {
    const fetchId = ++fetchIdRef.current
    const isStale = () => fetchIdRef.current !== fetchId

    if (!claimNumber) {
      setParticipants(initialSlice)
      setAdhocContacts(initialSlice)
      return
    }

    setParticipants(toLoading)
    setAdhocContacts(toLoading)

    let token: string
    try {
      token = await getToken()
    } catch (err: unknown) {
      if (isStale()) return
      console.error('[ClaimContacts] Failed to get token:', err)
      const msg = getApiErrorMessage(err, 'Authentication failed. Please try again.')
      setParticipants((prev) => toError(prev, msg))
      setAdhocContacts((prev) => toError(prev, msg))
      return
    }

    getClaimParticipants(claimNumber, token)
      .then((data) => {
        if (!isStale()) setParticipants({ data, loading: false, error: null })
      })
      .catch((err: unknown) => {
        if (isStale()) return
        console.error('[ClaimContacts] Failed to load participants:', err)
        const msg = getApiErrorMessage(err, 'Failed to load participants.')
        setParticipants((prev) => toError(prev, msg))
      })

    getClaimAdhocContacts(claimNumber, token)
      .then((data) => {
        if (!isStale()) setAdhocContacts({ data, loading: false, error: null })
      })
      .catch((err: unknown) => {
        if (isStale()) return
        console.error('[ClaimContacts] Failed to load ad-hoc contacts:', err)
        const msg = getApiErrorMessage(err, 'Failed to load ad-hoc contacts.')
        setAdhocContacts((prev) => toError(prev, msg))
      })
  }, [claimNumber, getToken])

  useEffect(() => {
    fetchContacts()
    return () => {
      fetchIdRef.current += 1
    }
    // Refetch only on claim change; fetchContacts is stable (memoized on claimNumber).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimNumber])

  return {
    participants,
    adhocContacts,
    retry: fetchContacts,
  }
}
